import { useGroupActivity, ActivityEvent } from '@/hooks/useGroupActivity';
import { ChevronRight } from 'lucide-react';
import { T } from '@/lib/tokens';

const SPEC_TYPES = ['loan_created', 'loan_returned', 'item_added', 'item_giving_away'] as const;
type SpecEventType = (typeof SPEC_TYPES)[number];

const DOT_COLOR: Record<SpecEventType, string> = {
  loan_created: T.warning,
  loan_returned: T.success,
  item_added: T.accent,
  item_giving_away: T.accent,
};

/**
 *
 * @param iso
 */
function formatAge(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  if (mins < 60) return `${Math.max(1, mins)}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;
  return `${weeks}w`;
}

/**
 *
 * @param root0
 * @param root0.event
 */
function EventText({ event }: { event: ActivityEvent & { type: SpecEventType } }) {
  const bold = (text: string) => <strong style={{ color: T.text, fontWeight: 600 }}>{text}</strong>;

  if (event.type === 'loan_created') {
    return (
      <>
        {bold(event.actorName)} borrowed {bold(event.itemName)} from{' '}
        {bold(event.targetName ?? 'someone')}
      </>
    );
  }
  if (event.type === 'loan_returned') {
    return (
      <>
        {bold(event.actorName)} returned {bold(event.itemName)}
      </>
    );
  }
  if (event.type === 'item_giving_away') {
    return (
      <>
        {bold(event.actorName)} is giving away {bold(event.itemName)}
      </>
    );
  }
  // item_added
  return (
    <>
      {bold(event.actorName)} added {bold(event.itemName)}
    </>
  );
}

/**
 *
 * @param root0
 * @param root0.event
 */
function FeedRow({ event }: { event: ActivityEvent & { type: SpecEventType } }) {
  return (
    <div
      style={{
        height: 38,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: DOT_COLOR[event.type],
          flexShrink: 0,
        }}
      />
      <span
        style={{
          fontSize: 13,
          color: T.textSec,
          flex: 1,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
      >
        <EventText event={event} />
      </span>
      <span
        style={{
          fontSize: 11,
          color: T.textMuted,
          flexShrink: 0,
        }}
      >
        {formatAge(event.timestamp)}
      </span>
    </div>
  );
}

interface ActivityFeedBandProps {
  groupId: string;
  onClick?: () => void;
}

/**
 *
 * @param root0
 * @param root0.groupId
 * @param root0.onClick
 */
export function ActivityFeedBand({ groupId, onClick }: ActivityFeedBandProps) {
  const { data: allEvents, isLoading } = useGroupActivity(groupId);

  if (isLoading) return null;

  const events = (allEvents ?? []).filter((e): e is ActivityEvent & { type: SpecEventType } =>
    (SPEC_TYPES as readonly string[]).includes(e.type),
  );

  if (events.length === 0) return null;

  const bandStyle: React.CSSProperties = {
    height: 38,
    overflow: 'hidden',
    background: T.accentSoft,
    borderBottom: `1px solid ${T.accentBorder}`,
    paddingLeft: 20,
    paddingRight: onClick ? 32 : 20,
    position: 'relative',
  };

  const chevron = onClick ? (
    <ChevronRight
      size={14}
      color={T.textMuted}
      style={{
        position: 'absolute',
        right: 12,
        top: '50%',
        transform: 'translateY(-50%)',
        pointerEvents: 'none',
      }}
    />
  ) : null;

  const Wrapper = onClick ? 'button' : 'div';
  const wrapperProps = onClick
    ? {
        onClick,
        style: {
          ...bandStyle,
          cursor: 'pointer',
          width: '100%',
          border: 'none',
          fontFamily: 'inherit',
          fontSize: 'inherit',
          textAlign: 'left' as const,
          display: 'block',
        },
      }
    : { style: bandStyle };

  // For single-item feeds, no animation needed — just show the item
  if (events.length === 1) {
    return (
      <Wrapper role="log" aria-live="polite" aria-atomic="true" {...wrapperProps}>
        <FeedRow event={events[0]} />
        {chevron}
      </Wrapper>
    );
  }

  // Duration: each item visible ~3s with ~0.5s transition = 3.5s per item
  const totalDuration = events.length * 3.5;
  // We duplicate the first item at end for seamless loop
  // Total scroll distance = events.length rows × 38px
  const trackItems = [...events, events[0]];

  const keyframesId = `activity-scroll-${groupId.replace(/[^a-z0-9]/gi, '')}`;
  const css = `
    @media (prefers-reduced-motion: no-preference) {
      .${keyframesId} {
        animation: ${keyframesId} ${totalDuration}s linear infinite;
      }
      @keyframes ${keyframesId} {
        0%   { transform: translateY(0); }
        100% { transform: translateY(calc(-38px * ${events.length})); }
      }
    }
  `;

  return (
    <Wrapper role="log" aria-live="polite" aria-atomic="true" {...wrapperProps}>
      <style>{css}</style>
      <div className={keyframesId} style={{ display: 'flex', flexDirection: 'column' }}>
        {trackItems.map((event, i) => (
          <FeedRow key={`${event.id}-${i}`} event={event} />
        ))}
      </div>
      {chevron}
    </Wrapper>
  );
}
